import { useState, useMemo } from "react";
import { Flex, Text, Button, Badge } from "@radix-ui/themes";
import { HamburgerMenuIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../hooks/useAuth";
import { useUserRole } from "../../hooks/useUserRole";
import { UserProfilePopover } from "../UserProfilePopover";
import styles from "./Toolbar.module.css";
import logoSvg from "../../../assets/logo.svg";

const fakeLocationKey = import.meta.env.VITE_FAKE_LOCATION;

interface MenuItem {
  labelKey: string;
  path: string;
  roles: ("admin" | "rep" | "auditor")[];
}

const menuItems: MenuItem[] = [
  { labelKey: "nav.manage", path: "/manage", roles: ["admin"] },
  { labelKey: "nav.proofing", path: "/proofing", roles: ["rep", "admin"] },
  { labelKey: "nav.approbation", path: "/approbation", roles: ["admin"] },
  { labelKey: "nav.audit", path: "/audit", roles: ["auditor"] },
];

export function Toolbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { role } = useUserRole();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filteredMenuItems = useMemo(() => {
    if (!role) return [];
    return menuItems.filter((item) => item.roles.includes(role));
  }, [role]);

  const handleNavigation = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarContent}>
        {/* Desktop Navigation */}
        <Flex align="center" gap="4" className={styles.desktopNav}>
          <Flex align="center" gap="2">
            <img src={logoSvg} alt="Promo Sight" className={styles.logo} />
            <Text size="2" weight="bold">
              Promo Sight
            </Text>
          </Flex>
          {filteredMenuItems.map((item) => (
            <Button
              key={item.path}
              variant="ghost"
              onClick={() => navigate(item.path)}
            >
              {t(item.labelKey)}
            </Button>
          ))}
        </Flex>

        {/* Mobile Hamburger Menu */}
        <Button
          variant="ghost"
          className={styles.hamburgerButton}
          onClick={() => setDrawerOpen(!drawerOpen)}
        >
          <HamburgerMenuIcon width="20" height="20" />
        </Button>

        {/* User Info and Sign Out */}
        <Flex align="center" gap="3" className={styles.desktopUser}>
          {fakeLocationKey && (
            <Badge color="orange" variant="soft">
              📍 {fakeLocationKey}
            </Badge>
          )}
          {user && (
            <>
              <UserProfilePopover />
              <Flex align="center" gap="2">
                <Text size="2">{user.email}</Text>
              </Flex>
              <Button
                variant="soft"
                onClick={() => {
                  signOut();
                  navigate("/");
                }}
              >
                {t("common.signOut")}
              </Button>
            </>
          )}
        </Flex>
      </div>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div className={styles.drawer}>
          <Flex direction="column" gap="3">
            <Flex align="center" gap="2">
              <img src={logoSvg} alt="Promo Sight" className={styles.logo} />
              <Text size="2" weight="bold">
                Promo Sight
              </Text>
            </Flex>
            {filteredMenuItems.map((item) => (
              <Button
                key={item.path}
                variant="ghost"
                onClick={() => handleNavigation(item.path)}
                style={{ justifyContent: "flex-start" }}
              >
                {t(item.labelKey)}
              </Button>
            ))}
            {fakeLocationKey && (
              <Badge color="orange" variant="soft">
                📍 {fakeLocationKey}
              </Badge>
            )}
            {user && (
              <>
                <UserProfilePopover />
                <Flex align="center" gap="2" px="2">
                  <Text size="2">{user.email}</Text>
                </Flex>
                <Button
                  variant="soft"
                  onClick={() => {
                    signOut();
                    navigate("/");
                    setDrawerOpen(false);
                  }}
                >
                  {t("common.signOut")}
                </Button>
              </>
            )}
          </Flex>
        </div>
      )}
    </div>
  );
}
